/**
 * Returns formatted semester label with explicit months to avoid confusion
 * for international exchange students.
 *
 * @param {number|string} semester - 1 or 2
 * @param {number|string} year - e.g. 2026, 2027
 * @param {object} texts - Optional translations object (texts.wizard)
 * @returns {string}
 */
export const getSemesterLabel = (semester, year, texts) => {
  if (!semester) return year ? `${year}` : '';
  const semNum = Number(semester);
  const yearStr = year ? String(year).trim() : '';

  if (semNum === 1) {
    if (texts?.wizard?.semester_1) {
      return texts.wizard.semester_1.replace('{year}', yearStr).replace(/\s+/g, ' ').trim();
    }
    return `Semestre marzo-julio ${yearStr}`.trim();
  }

  if (semNum === 2) {
    if (texts?.wizard?.semester_2) {
      return texts.wizard.semester_2.replace('{year}', yearStr).replace(/\s+/g, ' ').trim();
    }
    return `Semestre agosto-diciembre ${yearStr}`.trim();
  }

  const prefix = texts?.wizard?.semester || 'Semestre';
  return `${prefix} ${semester}${yearStr ? ` ${yearStr}` : ''}`.trim();
};

/**
 * Formats a destination name with explicit semester months and year.
 * Examples:
 * - "Santiago - Semestre agosto-diciembre 2026"
 * - "Santiago - Semestre marzo-julio 2027"
 *
 * @param {object} dest - Destination object { name, year, semester }
 * @param {object} texts - Optional translations object
 * @returns {string}
 */
export const formatDestinationName = (dest, texts) => {
  if (!dest) return '';
  const name = dest.name || '';
  const semesterLabel = getSemesterLabel(dest.semester, dest.year, texts);
  if (semesterLabel) {
    return `${name} - ${semesterLabel}`;
  }
  return name;
};

/**
 * Sorts destinations chronologically:
 * 1. Name alphabetically
 * 2. Year ascending
 * 3. Semester ascending
 *
 * @param {Array} destinations
 * @returns {Array} sorted copy of destinations
 */
export const sortDestinations = (destinations = []) => {
  if (!Array.isArray(destinations)) return [];
  return [...destinations].sort((a, b) => {
    const nameComp = (a.name || '').localeCompare(b.name || '');
    if (nameComp !== 0) return nameComp;
    if ((a.year || 0) !== (b.year || 0)) {
      return (a.year || 0) - (b.year || 0);
    }
    return (a.semester || 0) - (b.semester || 0);
  });
};
